import { usePassportProvider } from "@/context";
import { ERC20_ABI, ERC721_ABI, ERC1155_ABI } from "@/data-access/tokens/abis";
import * as checkout from "@imtbl/checkout-sdk";
import { Contract, formatUnits } from "ethers";
import { useCallback, useMemo, useRef } from "react";

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface CacheOptions {
  balanceStaleTime?: number; // in milliseconds
}

export interface ERC20Balance {
  balance: string;
  formatted: string;
  decimals: bigint;
  symbol?: string;
}

export interface BalanceError extends Error {
  code?: string;
  data?: string;
  transaction?: unknown;
}

export function useUserBalance(options: CacheOptions = {}) {
  const { zkEvmProvider } = usePassportProvider();
  const cacheRef = useRef<Map<string, CacheEntry<unknown>>>(new Map());
  const { balanceStaleTime = 0 } = options; // default to no cache

  const provider = useMemo(() => {
    if (!zkEvmProvider) return null;
    return new checkout.WrappedBrowserProvider(zkEvmProvider);
  }, [zkEvmProvider]);

  const getCacheEntry = useCallback(
    <T>(key: string): T | null => {
      const entry = cacheRef.current.get(key);
      if (!entry) return null;

      const isExpired = Date.now() - entry.timestamp > balanceStaleTime;
      if (isExpired) {
        cacheRef.current.delete(key);
        return null;
      }

      return entry.data as T;
    },
    [balanceStaleTime],
  );

  const setCacheEntry = useCallback(<T>(key: string, data: T): void => {
    cacheRef.current.set(key, {
      data,
      timestamp: Date.now(),
    });
  }, []);

  const clearCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  const getERC20Balance = useCallback(
    async (
      walletAddress: `0x${string}` | undefined,
      tokenAddress: string,
    ): Promise<ERC20Balance> => {
      try {
        if (!walletAddress) {
          throw new Error("Wallet address is required");
        }

        if (!provider) {
          throw new Error("Provider is not initialized");
        }

        const cacheKey = `erc20_${walletAddress.toLowerCase()}_${tokenAddress.toLowerCase()}`;
        const cachedBalance = getCacheEntry<ERC20Balance>(cacheKey);
        if (cachedBalance) {
          return cachedBalance;
        }

        const contract = new Contract(
          tokenAddress.toLowerCase(),
          ERC20_ABI,
          provider,
        );
        const [balance, decimals, symbol] = await Promise.all([
          contract.balanceOf(walletAddress.toLowerCase()),
          contract.decimals(),
          contract.symbol(),
        ]);

        const result = {
          balance: balance.toString(),
          formatted: formatUnits(balance, decimals),
          decimals,
          symbol,
        };

        setCacheEntry(cacheKey, result);
        return result;
      } catch (error) {
        const balanceError = error as BalanceError;
        throw new Error(
          `Failed to get ERC20 balance: ${balanceError.message || "Unknown error"}`,
        );
      }
    },
    [provider, getCacheEntry, setCacheEntry],
  );

  /**
   * hasERC721
   * @param walletAddress - The wallet address to check the balance for
   * @param contractAddress - The contract address to check the balance for
   * @param tokenId - The optional token ID to check the user owns. If not provided, the total balance of tokens the user owns in the collection will be checked.
   * @returns True if the user owns a specific token or any tokens in the collection, false otherwise
   */
  const hasERC721 = useCallback(
    async (
      walletAddress: `0x${string}` | undefined,
      contractAddress: string,
      tokenId?: number,
    ): Promise<boolean> => {
      try {
        if (!walletAddress) {
          throw new Error("Wallet address is required");
        }

        if (!provider) {
          throw new Error("Provider is not initialized");
        }

        const cacheKey = `erc721_${walletAddress.toLowerCase()}_${contractAddress.toLowerCase()}_${tokenId ?? "all"}`;
        const cachedResult = getCacheEntry<boolean>(cacheKey);
        if (cachedResult !== null) {
          return cachedResult;
        }

        const contract = new Contract(
          contractAddress.toLowerCase(),
          ERC721_ABI,
          provider,
        );

        // If tokenId is provided, check specific token ownership
        if (tokenId) {
          try {
            const owner = await contract.ownerOf(tokenId);
            const result = owner.toLowerCase() === walletAddress.toLowerCase();
            setCacheEntry(cacheKey, result);
            return result;
          } catch (error) {
            // If ownerOf fails, token doesn't exist or is burned
            return false;
          }
        }

        const balance = await contract.balanceOf(walletAddress.toLowerCase());
        const result = balance > BigInt(0);
        setCacheEntry(cacheKey, result);
        return result;
      } catch (error) {
        const balanceError = error as BalanceError;
        throw new Error(
          `Failed to check ERC721 ownership: ${balanceError.message || "Unknown error"}`,
        );
      }
    },
    [provider, getCacheEntry, setCacheEntry],
  );

  /**
   * getERC1155Balance
   * @param walletAddress - The wallet address to get the balance for
   * @param contractAddress - The contract address to get the balance for
   * @param tokenIds - Array of token IDs to get balances for. Use a single ID for individual balance or multiple IDs for batch balance check.
   * @returns Array of token balances as strings
   */
  const getERC1155Balance = useCallback(
    async (
      walletAddress: `0x${string}` | undefined,
      contractAddress: string,
      tokenIds: number[],
    ): Promise<string[]> => {
      if (!walletAddress) {
        throw new Error("Wallet address is required");
      }

      if (!provider) {
        throw new Error("Provider is not initialized");
      }

      if (tokenIds.length === 0) {
        return [];
      }

      try {
        const contract = new Contract(contractAddress, ERC1155_ABI, provider);
        // Handle single tokenId using balanceOf
        if (tokenIds.length === 1) {
          const tokenId = tokenIds[0];
          const cacheKey = `erc1155_${walletAddress.toLowerCase()}_${contractAddress.toLowerCase()}_${tokenId}`;
          const cachedBalance = getCacheEntry<string>(cacheKey);

          if (cachedBalance) {
            return [cachedBalance];
          }

          const balance = await contract.balanceOf(walletAddress, tokenId);
          const result = balance.toString();

          setCacheEntry(cacheKey, result);
          return [result];
        }

        // Handle multiple tokenIds using balanceOfBatch
        const tokenIdsKey = tokenIds.join("_");
        const cacheKey = `erc1155_batch_${walletAddress.toLowerCase()}_${contractAddress.toLowerCase()}_${tokenIdsKey}`;

        const cachedBalances = getCacheEntry<string[]>(cacheKey);
        if (cachedBalances) {
          return cachedBalances;
        }

        const accounts = Array(tokenIds.length).fill(walletAddress);

        const balances = await contract.balanceOfBatch(accounts, tokenIds);

        const results = balances.map((balance: bigint) => balance.toString());

        setCacheEntry(cacheKey, results);
        return results;
      } catch (error) {
        const balanceError = error as BalanceError;
        throw new Error(
          `Failed to get ERC1155 balance: ${balanceError.message || "Unknown error"}`,
        );
      }
    },
    [provider, getCacheEntry, setCacheEntry],
  );

  /**
   * getERC721Balance
   * @param walletAddress - The wallet address to get the balance for
   * @param contractAddress - The contract address to get the balance for
   * @param tokenId - The optional token ID to get the balance for. If not provided, the total balance of tokens the user owns in the collection will be returned.
   * @returns The users balance of the ERC721 token
   */
  const getERC721Balance = useCallback(
    async (
      walletAddress: `0x${string}` | undefined,
      contractAddress: string,
      tokenId?: number,
    ): Promise<string> => {
      try {
        if (!walletAddress) {
          throw new Error("Wallet address is required");
        }

        if (!provider) {
          throw new Error("Provider is not initialized");
        }

        const cacheKey = `erc721_balance_${walletAddress.toLowerCase()}_${contractAddress.toLowerCase()}_${tokenId ?? "all"}`;
        const cachedBalance = getCacheEntry<string>(cacheKey);
        if (cachedBalance) {
          return cachedBalance;
        }

        const contract = new Contract(
          contractAddress.toLowerCase(),
          ERC721_ABI,
          provider,
        );

        // If tokenId is provided, check specific token ownership
        if (tokenId !== undefined) {
          try {
            const owner = await contract.ownerOf(tokenId);
            const result =
              owner.toLowerCase() === walletAddress.toLowerCase() ? "1" : "0";
            setCacheEntry(cacheKey, result);
            return result;
          } catch (error) {
            // If ownerOf fails, token doesn't exist or is burned
            return "0";
          }
        }

        // If no tokenId, get the total balance of tokens from this collection
        const balance = await contract.balanceOf(walletAddress.toLowerCase());
        const result = balance.toString();
        setCacheEntry(cacheKey, result);
        return result;
      } catch (error) {
        const balanceError = error as BalanceError;
        throw new Error(
          `Failed to get ERC721 balance: ${balanceError.message || "Unknown error"}`,
        );
      }
    },
    [provider, getCacheEntry, setCacheEntry],
  );

  return useMemo(
    () => ({
      getERC20Balance,
      hasERC721,
      getERC1155Balance,
      getERC721Balance,
      clearCache,
    }),
    [
      getERC20Balance,
      hasERC721,
      getERC1155Balance,
      getERC721Balance,
      clearCache,
    ],
  );
}
